import React, { useState } from "react";
import { IResourceComponentsProps } from "@pankod/refine-core";
import { 
  List, 
  Card, 
  Space, 
  Typography, 
  Table, 
  Tag, 
  Button, 
  Popconfirm, 
  Alert, 
  Empty, 
  Spin,
  Checkbox
} from "@pankod/refine-antd";
import { 
  DeleteOutlined, 
  SearchOutlined, 
  SafetyCertificateOutlined,
  WarningOutlined,
  ClearOutlined
} from "@ant-design/icons";
import { findMeilisearchDuplicates, cleanupMeilisearchDuplicates } from "Services/productServices";
import toast from "react-hot-toast";

const { Text, Title, Paragraph } = Typography;

export const SearchDuplicates: React.FC<IResourceComponentsProps> = () => {
  const [duplicates, setDuplicates] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [summary, setSummary] = useState<any>(null);

  const handleScan = async () => {
    setLoading(true);
    setDuplicates([]);
    setSelectedIds([]);
    setSummary(null);
    try {
      const response = await findMeilisearchDuplicates();
      if (response.data.success) {
        setDuplicates(response.data.duplicates);
        setSummary(response.data.summary);
        if (response.data.summary.duplicateSkuCount === 0) {
          toast.success("Meilisearch index is healthy! No duplicates found.");
        } else {
          toast.success(`Found ${response.data.summary.duplicateSkuCount} fragmented SKUs`);
        }
      } else {
        toast.error("Failed to scan Meilisearch index");
      }
    } catch (error) {
      console.error("Meilisearch scan failed:", error);
      toast.error("Error connecting to bridge");
    } finally {
      setLoading(false);
    }
  };

  const handleCleanup = async () => {
    if (selectedIds.length === 0) {
      toast.error("No orphaned documents selected for cleanup");
      return;
    }

    setCleaning(true);
    try {
      const response = await cleanupMeilisearchDuplicates(selectedIds);
      if (response.data.success) {
        toast.success(`Successfully deleted ${response.data.summary.cleanedCount} orphaned documents`);
        handleScan();
      } else {
        toast.error("Search index cleanup failed");
      }
    } catch (error) {
      console.error("Search cleanup failed:", error);
      toast.error("Error during cleanup operation");
    } finally {
      setCleaning(false);
    }
  };

  const autoSelectOrphans = () => {
    const idsToSelect: string[] = [];
    duplicates.forEach(group => {
      // In each group, select anything that isn't the "Correct ID"
      group.documents.forEach((doc: any) => {
        if (!doc.isCorrectId) {
          idsToSelect.push(doc.id);
        }
      });
    });
    setSelectedIds(idsToSelect);
    toast.success(`Selected ${idsToSelect.length} orphaned documents for deletion`);
  };

  const toggleSelection = (id: string) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const expandedRowRender = (record: any) => {
    const columns = [
      {
        title: "Select",
        key: "select",
        render: (_: any, item: any) => (
          <Checkbox 
            checked={selectedIds.includes(item.id)}
            onChange={() => toggleSelection(item.id)}
          />
        ),
        width: 60
      },
      {
        title: "Document ID",
        dataIndex: "id",
        key: "id",
        width: 150,
        render: (id: string, item: any) => (
          <Space>
            <Text code>{id}</Text>
            {item.isCorrectId ? (
              <Tag color="success">Correct (SKU-based)</Tag>
            ) : (
              <Tag color="error">Orphan (Likely Strapi ID)</Tag>
            )}
          </Space>
        )
      },
      {
        title: "Price",
        dataIndex: "price",
        key: "price",
        render: (val: any) => val ? `$${Number(val).toFixed(2)}` : "N/A",
        width: 100
      },
      {
        title: "Stock",
        dataIndex: "stock",
        key: "stock",
        render: (val: any) => <Text strong>{val !== undefined ? val : "N/A"}</Text>,
        width: 80
      },
      {
        title: "Status",
        key: "status",
        render: (_: any, item: any) => (
          item.isCorrectId 
            ? <Text type="success"><SafetyCertificateOutlined /> Valid Representation</Text>
            : <Text type="danger"><WarningOutlined /> Outdated ID Format</Text>
        )
      }
    ];

    return (
      <Table 
        columns={columns} 
        dataSource={record.documents} 
        pagination={false} 
        rowKey="id"
        size="small"
      />
    );
  };

  const mainColumns = [
    {
      title: "Master SKU",
      dataIndex: "masterSku",
      key: "masterSku",
      render: (sku: string) => <Text strong>{sku}</Text>
    },
    {
      title: "Product Name",
      key: "name",
      render: (_: any, record: any) => record.documents[0]?.name || "N/A"
    },
    {
      title: "Index Copies",
      key: "count",
      render: (_: any, record: any) => (
        <Tag color="red">{record.documents.length} Docs</Tag>
      )
    },
    {
      title: "Healing Status",
      key: "healing",
      render: (_: any, record: any) => {
        const hasCorrectId = record.documents.some((d: any) => d.isCorrectId);
        return hasCorrectId 
          ? <Tag color="orange">Can Be Healed (Keep 1, Purge others)</Tag> 
          : <Tag color="error">Missing Valid SKU ID Format!</Tag>;
      }
    }
  ];

  return (
    <List 
      title="Search Index Cleanup (Meilisearch)"
      headerProps={{
        extra: (
          <Space>
            <Button 
              icon={<SearchOutlined />} 
              onClick={handleScan}
              loading={loading}
              type="primary"
            >
              Scan Search Index
            </Button>
            {duplicates.length > 0 && (
              <>
                <Button icon={<ClearOutlined />} onClick={autoSelectOrphans}>
                  Auto-Select All Orphans
                </Button>
                <Popconfirm
                  title={`Delete ${selectedIds.length} orphaned documents from Search Index?`}
                  onConfirm={handleCleanup}
                  okText="Yes, Delete"
                  cancelText="No"
                  okButtonProps={{ danger: true, loading: cleaning }}
                >
                  <Button 
                    danger 
                    icon={<DeleteOutlined />} 
                    disabled={selectedIds.length === 0}
                  >
                    Delete Selected ({selectedIds.length})
                  </Button>
                </Popconfirm>
              </>
            )}
          </Space>
        )
      }}
    >
      <Alert
        message="Resolving Search Fragmentation"
        description={
          <Paragraph>
            When the Bridge transitioned to using SKU-based document IDs instead of Strapi numeric IDs, many "orphaned" search documents were left behind. 
            This causes the search results to show the same product multiple times. This tool identifies documents that don't match our 
            current ID convention and purges them safely.
          </Paragraph>
        }
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      {summary && summary.totalOrphanedDocs > 0 && (
        <Alert
          message="Deep Clean Recommended"
          description={`Total of ${summary.totalOrphanedDocs} orphaned records found across ${summary.duplicateSkuCount} fragmented SKUs.`}
          type="warning"
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      <Card>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" tip="Reading Meilisearch document list... scanning index fragmentation." />
          </div>
        ) : duplicates.length > 0 ? (
          <Table 
            dataSource={duplicates} 
            columns={mainColumns} 
            expandable={{
              expandedRowRender,
              defaultExpandAllRows: true
            }}
            rowKey="masterSku"
          />
        ) : (
          <Empty 
            description="Meilisearch index is clear of ID conflicts." 
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Button type="primary" onClick={handleScan} icon={<SearchOutlined />}>
              Perform Deep Scan
            </Button>
          </Empty>
        )}
      </Card>
    </List>
  );
};
